import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ProgressBar } from '@/components/ui/progress-bar';
import { Skeleton } from '@/components/ui/skeleton';
import { formatDate, formatMoney, storageDisplayParts } from '@/utils/format';

import type { SubscriptionsSummary } from './subscriptions-types';

const UsageMetric = ({
  label,
  action,
  value,
  extra,
  children,
}: {
  label: string;
  action?: ReactNode;
  value: string;
  extra?: ReactNode;
  children: ReactNode;
}) => (
  <div className="flex flex-col gap-2">
    <div className="flex min-h-8 items-baseline justify-between gap-2">
      <p className="text-caption text-fg-secondary">{label}</p>
      {action}
    </div>
    <div className="space-y-1">
      <p className="text-body-sm font-medium text-fg">{value}</p>
      {extra}
    </div>
    {children}
  </div>
);

const storageQuotaGib = (quotaBytes: number): number => Math.round(quotaBytes / 1024 ** 3);

const formatStorageAmount = (
  bytes: number,
  t: (key: string, options?: Record<string, unknown>) => string,
): string => {
  const { size, unit } = storageDisplayParts(bytes);
  return unit === 'gb' ? t('storageAmountGb', { size }) : t('storageAmountMb', { size });
};

export const SubscriptionsSummaryStrip = ({
  data,
  loading,
  onAddSeat,
}: {
  data?: SubscriptionsSummary;
  loading: boolean;
  onAddSeat?: () => void;
}) => {
  const { t } = useTranslation('billing');

  if (loading) {
    return <Skeleton className="h-28 w-full rounded-xl" />;
  }

  if (!data) return null;

  const { subscription, storage } = data;
  const canRenewCurrentPlan = data.management?.canRenewCurrentPlan ?? false;
  const periodValue = subscription.trialEndsAt ?? subscription.currentPeriodEnd;
  const showSeatBreakdown = subscription.purchasedSeats > 0;

  return (
    <Card className="overflow-hidden border-brand/15 bg-gradient-to-br from-brand/8 via-surface to-surface">
      <div className="grid items-start gap-5 p-5 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)]">
        <div className="space-y-2">
          <p className="text-caption font-medium uppercase tracking-wide text-fg-secondary">
            {t('summary.currentPlan')}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-heading-sm font-semibold text-fg">
              {t(`plans.${subscription.plan}`)}
            </h2>
            <Badge
              variant={
                subscription.status === 'active' || subscription.status === 'trialing'
                  ? 'success'
                  : subscription.status === 'past_due' ||
                      subscription.status === 'grace' ||
                      subscription.status === 'suspended'
                    ? 'warning'
                    : 'neutral'
              }
            >
              {t(`statuses.${subscription.status}`)}
            </Badge>
          </div>
          {periodValue ? (
            <p className="text-body-sm text-fg-secondary">
              {subscription.cancelAtPeriodEnd
                ? t('cancelEndsOn', { date: formatDate(periodValue) })
                : subscription.trialEndsAt
                  ? t('trialEndsOn', { date: formatDate(periodValue) })
                  : canRenewCurrentPlan
                    ? t('overview.renewAvailable', { date: formatDate(periodValue) })
                    : subscription.currentPeriodEnd
                      ? t('periodEndsOn', { date: formatDate(periodValue) })
                      : null}
            </p>
          ) : null}
          {data.management?.pendingPlan && subscription.currentPeriodEnd ? (
            <p className="text-body-sm text-brand">
              {t('management.downgradePendingDescription', {
                plan: t(`plans.${data.management.pendingPlan}`),
                date: formatDate(subscription.currentPeriodEnd),
              })}
            </p>
          ) : null}
        </div>

        <div className="grid items-start gap-4 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
          <UsageMetric
            label={t('seatsLabel')}
            action={
              data.management?.canAddPurchasedSeat && onAddSeat ? (
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-auto min-h-0 px-0 py-0 text-caption font-medium"
                  onClick={onAddSeat}
                >
                  {t('seats.addAction')}
                </Button>
              ) : undefined
            }
            value={
              showSeatBreakdown
                ? t('seatsUsedBreakdown', {
                    used: subscription.seatsInUse,
                    total: subscription.totalSeats,
                    included: subscription.includedSeats,
                    purchased: subscription.purchasedSeats,
                  })
                : t('seatsUsed', {
                    used: subscription.seatsInUse,
                    total: subscription.totalSeats,
                  })
            }
            extra={
              subscription.pendingProrationMinor > 0 ? (
                <p className="text-caption text-fg-secondary">
                  {t('seats.pendingProration', {
                    amount: formatMoney(subscription.pendingProrationMinor, 'GHS'),
                  })}
                </p>
              ) : null
            }
          >
            <ProgressBar
              value={subscription.seatsInUse}
              max={Math.max(subscription.totalSeats, 1)}
              label={t('seatsLabel')}
              valueText={t('seatsAvailable', { count: subscription.seatsAvailable })}
            />
          </UsageMetric>

          {storage ? (
            <UsageMetric
              label={t('storage')}
              value={
                storage.quotaBytes === null
                  ? t('storageQuotaConsult')
                  : t('storageQuota', { size: storageQuotaGib(storage.quotaBytes) })
              }
            >
              {storage.quotaBytes === null ? (
                <p className="text-caption text-fg-secondary">
                  {t('storageUsedOnly', {
                    used: formatStorageAmount(storage.usedBytes, t),
                  })}
                </p>
              ) : (
                <ProgressBar
                  value={storage.usedBytes}
                  max={Math.max(storage.quotaBytes, 1)}
                  label={t('storage')}
                  valueText={t('storageUsedOfQuota', {
                    used: formatStorageAmount(storage.usedBytes, t),
                    total: formatStorageAmount(storage.quotaBytes, t),
                  })}
                />
              )}
            </UsageMetric>
          ) : null}
        </div>
      </div>
    </Card>
  );
};
